// Tabla de precios que edita Diego: precio de lista por watt según el tamaño (aprendido de cotizaciones pasadas),
// costos fijos (UVIE + UI en media tensión, trámites…), costos internos para la rentabilidad, umbrales del semáforo
// (también los de costos fijos, contrato v2), el panel por omisión y, opcionales, las baterías y los inversores
// híbridos del respaldo. Cada guardado es una versión nueva (nunca se borra una tabla). Se valida con el MISMO esquema
// que el servidor antes de mandarla.
import { useEffect, useState } from 'react';
import { BatteryCharging, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { CuerpoTablaPrecios, DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION, DESCUENTO_MAX_SIN_APROBACION_POR_OMISION, ESTRUCTURAS_POR_OMISION, ETIQUETA_ESTRUCTURA, PRECIO_UNICO_ARRIBA_DE_POR_OMISION, TARIFAS_MEDIA_TENSION, TARIFAS_NEGOCIO, TIPOS_PROYECTO, fechaHoraCorta, umbralesCostosFijos } from '@cotizador/compartido';
import type { CostoFijo, PrecioBateria, PrecioInversorHibrido, RespuestaPrecios, TablaPrecios, Tarifa, TipoProyecto } from '@cotizador/compartido';
import type { z } from 'zod';
import { api } from '../../api/consultas.ts';
import { Boton, CampoTexto, Cargando, Chip, ErrorEnLinea, Interruptor, Seccion } from '../../componentes/ui.tsx';
import { ETIQUETA_TIPO } from '../../logica/etiquetas.ts';
import { numeroDe } from '../../logica/cuestionario.ts';

type Cuerpo = Omit<TablaPrecios, 'version' | 'vigente_desde' | 'editada_por'>;

/** Campo numérico que deja teclear a medias ("1.") y reporta number | null (vacío) | NaN (no válido). */
function NumeroCampo({
  etiqueta,
  valor,
  alCambiar,
  sufijo,
  ayuda,
  error,
  chico,
  placeholder,
}: {
  etiqueta: string;
  valor: number | null;
  alCambiar: (n: number | null) => void;
  sufijo?: string;
  ayuda?: string;
  error?: string;
  chico?: boolean;
  placeholder?: string;
}) {
  const [texto, setTexto] = useState(valor === null || Number.isNaN(valor) ? '' : String(valor));
  useEffect(() => {
    const actual = numeroDe(texto);
    if (!(actual === valor || (Number.isNaN(actual) && Number.isNaN(valor)))) setTexto(valor === null || Number.isNaN(valor) ? '' : String(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  return (
    <CampoTexto
      // Hallazgo del 10-oct: en un campo chico el sufijo se comía el número (a 390 px quedaban 2 px). Ahí la unidad va
      // en la etiqueta; en los campos anchos se queda adentro.
      etiqueta={chico && sufijo ? `${etiqueta} (${sufijo})` : etiqueta}
      inputMode="decimal"
      sufijo={chico ? undefined : sufijo}
      ayuda={ayuda}
      className={chico ? 'campo--chico' : ''}
      placeholder={placeholder}
      value={texto}
      error={error ?? (texto && Number.isNaN(numeroDe(texto)) ? 'Número no válido' : null)}
      onChange={(e) => {
        setTexto(e.target.value);
        alCambiar(numeroDe(e.target.value));
      }}
    />
  );
}

function sinMeta(t: TablaPrecios): Cuerpo {
  const { version: _v, vigente_desde: _d, editada_por: _e, ...resto } = t;
  return structuredClone(resto);
}

/** Nombres de los campos para leer un problema del esquema ("Batería 2 › kWh útiles por módulo: falta un número"). */
const NOMBRE_CAMPO: Record<string, string> = {
  rangos: 'Precio por watt',
  costos_fijos: 'Costo fijo',
  baterias: 'Batería',
  inversores_hibridos: 'Inversor híbrido',
  umbrales: 'Umbrales',
  costos_internos: 'Costos internos',
  costos_por_tamano: 'Costos por tamaño',
  estructuras: 'Estructuras',
  panel_por_omision: 'Panel por omisión',
  kwp_desde: 'desde (kWp)',
  kwp_hasta: 'hasta (kWp)',
  precio_lista_sin_iva_por_w: 'precio por watt',
  concepto: 'concepto',
  precio_cliente_sin_iva: 'precio al cliente',
  costo_solarix_sin_iva: 'costo para Solarix',
  id: 'clave',
  marca: 'marca',
  modelo: 'modelo',
  quimica: 'química',
  kwh_nominales_por_modulo: 'kWh nominales por módulo',
  kwh_utiles_por_modulo: 'kWh útiles por módulo',
  modulos_max: 'módulos máximo',
  precio_lista_sin_iva_por_modulo: 'precio de lista por módulo',
  costo_solarix_sin_iva_por_modulo: 'costo para Solarix por módulo',
  garantia_anios: 'garantía',
  kw_continuos: 'kW continuos',
  ms_cambio: 'cambio a respaldo',
  precio_lista_sin_iva: 'precio de lista',
  fuente: 'fuente',
  costos_fijos_alerta_pct: 'alerta de costos fijos',
  costos_fijos_rojo_pct: 'rojo de costos fijos',
  margen_minimo_pct: 'margen mínimo',
  margen_alerta_pct: 'margen de alerta',
  descuento_max_sin_aprobacion_pct: 'tope sin OK de los demás vendedores',
  descuento_max_sin_aprobacion_internos_pct: 'tope sin OK de Diego, Miguel y Juan Carlos',
  otros_pct: 'imprevistos',
  descuento_contado_pct: 'Descuento de contado',
  precio_unico_arriba_de_con_iva: 'Precio único arriba de (con IVA)',
  generacion_kwh_kwp: 'Generación (kWh por kWp al año, máximo 1,750)',
  w: 'watts',
  nota: 'Nota',
};

/** Un problema del esquema en español: zod los escribe en inglés ("Too small: expected string…"). */
export function problemaLegible(i: z.core.$ZodIssue): string {
  const partes = i.path.map((p) => (typeof p === 'number' ? String(p + 1) : (NOMBRE_CAMPO[String(p)] ?? String(p))));
  const donde = partes.length ? partes.join(' › ').replace(/ › (\d+)/g, ' $1') : 'Tabla';
  let que = i.message;
  if (i.code === 'invalid_type') que = i.expected === 'int' ? 'debe ser un número entero' : 'falta un número válido';
  else if (i.code === 'too_small') que = i.origin === 'string' ? `muy corto (mínimo ${String(i.minimum)} letras)` : Number(i.minimum) === 0 && !i.inclusive ? 'debe ser mayor que 0' : `debe ser ${i.inclusive ? 'al menos' : 'mayor que'} ${String(i.minimum)}`;
  else if (i.code === 'too_big') que = i.origin === 'string' ? `muy largo (máximo ${String(i.maximum)} letras)` : `debe ser ${i.inclusive ? 'a lo más' : 'menor que'} ${String(i.maximum)}`;
  else if (i.code === 'invalid_format') que = 'solo minúsculas, números y guiones (de 2 a 40)';
  return `${donde}: ${que}`;
}

/** Siguiente clave libre ("bateria-3") para una lista con ids. */
function claveLibre(prefijo: string, usadas: readonly { id: string }[]): string {
  for (let n = usadas.length + 1; ; n++) if (!usadas.some((x) => x.id === `${prefijo}-${n}`)) return `${prefijo}-${n}`;
}

function nuevaBateria(usadas: readonly { id: string }[]): PrecioBateria {
  return {
    id: claveLibre('bateria', usadas),
    marca: '',
    modelo: '',
    quimica: 'LFP',
    kwh_nominales_por_modulo: Number.NaN,
    kwh_utiles_por_modulo: Number.NaN,
    modulos_max: Number.NaN,
    precio_lista_sin_iva_por_modulo: Number.NaN,
    costo_solarix_sin_iva_por_modulo: null,
    // 10 = la de casi todas las baterías (Fortress, Huawei, Victron y Pylontech: adenda A2 y ADENDA z3). Growatt es 5 años,
    // ampliables a 10 con su garantía extendida (ADENDA z2, 14-oct-2026): Diego la corrige al capturarla. Lo que imprime el
    // PDF sale de la plantilla (lib/nucleo/garantias.json), no de este campo.
    garantia_anios: 10,
    fuente: 'Diego',
    confirmado: false,
  };
}

function nuevoInversor(usadas: readonly { id: string }[]): PrecioInversorHibrido {
  return {
    id: claveLibre('inversor', usadas),
    marca: '',
    modelo: '',
    kw_continuos: Number.NaN,
    ms_cambio: null,
    precio_lista_sin_iva: Number.NaN,
    costo_solarix_sin_iva: null,
    garantia_anios: 10,
    fuente: 'Diego',
    confirmado: false,
  };
}

type Poner = (f: (x: Cuerpo) => void) => void;

/** Umbrales de COSTOS FIJOS (contrato v2): % de los fijos sobre la lista sin IVA de la opción. Van los dos o ninguno;
 *  sin ellos valen los de omisión (sin confirmar), y la pantalla lo dice. Lo que vale HOY sale de
 *  umbralesCostosFijos(tabla guardada), la misma función con la que leen el motor y la IA. */
function UmbralesFijos({ t, guardada, poner }: { t: Cuerpo; guardada: TablaPrecios; poner: Poner }) {
  const a = t.umbrales.costos_fijos_alerta_pct;
  const r = t.umbrales.costos_fijos_rojo_pct;
  const vigentes = umbralesCostosFijos(guardada);
  // Si la tabla guardada ya trae los suyos, "volver a los de omisión" no tiene a dónde volver más que al valor de omisión:
  // los de omisión son los que dan umbralesCostosFijos sobre una tabla SIN los dos campos.
  const om = umbralesCostosFijos({ umbrales: {} });
  const deTabla = a !== undefined || r !== undefined;
  // Lo GUARDADO solo se repite cuando el borrador ya no dice lo mismo (si no, el chip y las ayudas ya lo dicen: tres veces
  // "por omisión, sin confirmar" en un mismo bloque era ruido).
  const cambiado = deTabla !== vigentes.de_tabla || (deTabla && (a !== vigentes.alerta_pct || r !== vigentes.rojo_pct));
  const faltaUno = 'Escribe los dos, o deja los dos vacíos para usar los de omisión.';
  const errA = a === undefined && r !== undefined ? faltaUno : undefined;
  const errR =
    r === undefined && a !== undefined
      ? faltaUno
      : a !== undefined && r !== undefined && Number.isFinite(a) && Number.isFinite(r) && r <= a
        ? `Debe ser mayor que la alerta (${a} %).`
        : undefined;
  const poner1 = (k: 'costos_fijos_alerta_pct' | 'costos_fijos_rojo_pct', n: number | null) =>
    poner((x) => {
      if (n === null) delete x.umbrales[k];
      else x.umbrales[k] = n;
    });
  return (
    <div className="umbrales-fijos">
      <div className="umbrales-fijos__cabeza">
        <p className="umbrales-fijos__titulo">Costos fijos (UVIE + UI, trámites)</p>
        {deTabla ? <Chip tono="verde">De esta tabla</Chip> : <Chip tono="ambar">Por omisión, sin confirmar</Chip>}
      </div>
      <p className="nota-suave">
        Cuánto pesan los costos fijos sobre el precio de lista sin IVA de cada opción. Desde la alerta, el semáforo sale amarillo; desde el rojo, aviso rojo.
        {cambiado &&
          ` Hasta que guardes la tabla siguen valiendo ${vigentes.alerta_pct} % y ${vigentes.rojo_pct} %${vigentes.de_tabla ? ' (de la tabla guardada)' : ' (por omisión, sin confirmar)'}.`}
      </p>
      <div className="rejilla-2">
        <NumeroCampo
          etiqueta="Alerta desde (amarillo)"
          sufijo="%"
          valor={a ?? null}
          placeholder={String(om.alerta_pct)}
          alCambiar={(n) => poner1('costos_fijos_alerta_pct', n)}
          error={errA}
          ayuda={deTabla ? undefined : `Vacío = ${om.alerta_pct} % (por omisión)`}
        />
        <NumeroCampo
          etiqueta="Rojo desde"
          sufijo="%"
          valor={r ?? null}
          placeholder={String(om.rojo_pct)}
          alCambiar={(n) => poner1('costos_fijos_rojo_pct', n)}
          error={errR}
          ayuda={deTabla ? undefined : `Vacío = ${om.rojo_pct} % (por omisión)`}
        />
      </div>
      <div className="fila-botones">
        {deTabla ? (
          <Boton
            chico
            variante="fantasma"
            icono={<RotateCcw aria-hidden />}
            onClick={() =>
              poner((x) => {
                delete x.umbrales.costos_fijos_alerta_pct;
                delete x.umbrales.costos_fijos_rojo_pct;
              })
            }
          >
            Volver a los de omisión
          </Boton>
        ) : (
          <Boton
            chico
            onClick={() =>
              poner((x) => {
                x.umbrales.costos_fijos_alerta_pct = om.alerta_pct;
                x.umbrales.costos_fijos_rojo_pct = om.rojo_pct;
              })
            }
          >
            Confirmar {om.alerta_pct} % y {om.rojo_pct} %
          </Boton>
        )}
      </div>
    </div>
  );
}

/** Diego, 12-oct: costo por panel de cada estructura (solo él ve esta pantalla). Losa/lámina = la estructura y la mano de
 *  obra de siempre; PTR, suelo y carport reemplazan a esas en su obra y lo que cuestan de más sube el precio del sistema
 *  (con el margen de la tabla y la comisión del vendedor). Si la tabla todavía no los trae, se enseñan los que dijo Diego
 *  el 12-oct y al tocar uno se guardan con la tabla. */
function Estructuras({ t, poner }: { t: Cuerpo; poner: Poner }) {
  const cp = t.costos_por_tamano;
  if (!cp) return null;
  const est = cp.estructuras ?? ESTRUCTURAS_POR_OMISION;
  const ponerEst = (tipo: 'ptr' | 'suelo' | 'carport', f: (e: (typeof est)['ptr']) => void) =>
    poner((x) => {
      const c = x.costos_por_tamano!;
      if (!c.estructuras) c.estructuras = structuredClone(ESTRUCTURAS_POR_OMISION) as NonNullable<typeof c.estructuras>;
      f(c.estructuras[tipo]);
    });
  return (
    <Seccion
      titulo="Estructuras (costo por panel)"
      descripcion="Lo que cuesta cada tipo de estructura. PTR, suelo y carport suben el precio del sistema con lo que cuestan de más que la losa o lámina, para cuidar el margen. Solo tú lo ves."
    >
      <div className="hoja pila">
        <p className="campo__etiqueta">{ETIQUETA_ESTRUCTURA.losa_lamina} (la de siempre)</p>
        <div className="rejilla-3">
          <NumeroCampo etiqueta="Estructura" sufijo="USD" valor={cp.estructura_usd_por_panel} alCambiar={(n) => poner((x) => void (x.costos_por_tamano!.estructura_usd_por_panel = n ?? Number.NaN))} />
          <NumeroCampo etiqueta="Mano de obra" sufijo="$" valor={cp.mano_obra_por_panel_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_por_tamano!.mano_obra_por_panel_sin_iva = n ?? Number.NaN))} />
        </div>
        {(['ptr', 'suelo', 'carport'] as const).map((tipo) => (
          <div key={tipo} className="pila pila--chica">
            <p className="campo__etiqueta">
              {ETIQUETA_ESTRUCTURA[tipo]} {!cp.estructuras && <Chip tono="ambar">la del 12-oct, sin guardar</Chip>}
            </p>
            <div className="rejilla-3">
              {/* Vacío = la misma de la losa (la sigue si la cambias): así va el PTR, aluminio de siempre sobre su base de acero.
                  Hallazgo del 12-oct: con 60 fijos, subir la losa a 73 BAJABA el precio del PTR. */}
              <NumeroCampo
                etiqueta="Estructura"
                sufijo="USD"
                valor={est[tipo].estructura_usd_por_panel}
                placeholder={`= losa (${cp.estructura_usd_por_panel})`}
                ayuda={est[tipo].estructura_usd_por_panel === null ? 'Vacío: la misma de la losa (si la cambias, esta también).' : undefined}
                alCambiar={(n) => ponerEst(tipo, (e) => void (e.estructura_usd_por_panel = n))}
              />
              <NumeroCampo etiqueta="Herrería" sufijo="USD" valor={est[tipo].herreria_usd_por_panel} alCambiar={(n) => ponerEst(tipo, (e) => void (e.herreria_usd_por_panel = n ?? Number.NaN))} />
              <NumeroCampo etiqueta="Mano de obra" sufijo="$" valor={est[tipo].mano_obra_por_panel_sin_iva} alCambiar={(n) => ponerEst(tipo, (e) => void (e.mano_obra_por_panel_sin_iva = n ?? Number.NaN))} />
            </div>
            <Interruptor
              etiqueta="Por confirmar"
              detalle="Mientras esté encendido, la cotización con esta estructura lleva un aviso amarillo (sin cifras de costo)."
              marcado={est[tipo].por_confirmar}
              alCambiar={(v) => ponerEst(tipo, (e) => void (e.por_confirmar = v))}
            />
          </div>
        ))}
      </div>
    </Seccion>
  );
}

/** Baterías (precio por MÓDULO) e inversores híbridos del respaldo. Opcionales: sin ellos el motor no inventa precio
 *  (los niveles salen sin precio, con aviso RESPALDO_SIN_PRECIO). */
function Respaldo({ t, poner }: { t: Cuerpo; poner: Poner }) {
  const bats = t.baterias ?? [];
  const invs = t.inversores_hibridos ?? [];
  const ponerBat = (i: number, f: (b: PrecioBateria) => void) => poner((x) => f(x.baterias![i]!));
  const ponerInv = (i: number, f: (v: PrecioInversorHibrido) => void) => poner((x) => f(x.inversores_hibridos![i]!));
  return (
    <Seccion
      titulo="Baterías y respaldo"
      descripcion="Opcional. Sin precios aquí, los niveles de respaldo salen sin precio y con aviso: nunca se inventan. Por omisión, cada nivel lleva la batería e inversor compatibles más baratos para el cliente (entre las marcas de calidad que Diego aprueba y con garantía en la propuesta); el precio por módulo y por inversor debe incluir sus cables, base y envío. La instalación va como costo fijo que aplica a Baterías / Aislado (el respaldo es de toda la casa: no hay tablero aparte)."
    >
      <div className="pila">
        {bats.length === 0 && invs.length === 0 && (
          <div className="vacio vacio--chico">
            <BatteryCharging aria-hidden className="vacio__icono" />
            <p className="vacio__texto">Todavía no hay baterías ni inversores híbridos en la tabla.</p>
          </div>
        )}
        {bats.map((b, i) => (
          <div key={i} className="hoja pila costo-fijo" aria-label={`Batería ${i + 1}`} role="group">
            <p className="costo-fijo__titulo">Batería {i + 1}{b.marca || b.modelo ? ` · ${b.marca} ${b.modelo}`.trimEnd() : ''}</p>
            <div className="rejilla-3">
              <CampoTexto etiqueta="Marca" placeholder="Fortress, Growatt, Huawei…" value={b.marca} onChange={(e) => ponerBat(i, (x) => void (x.marca = e.target.value))} />
              <CampoTexto etiqueta="Modelo" value={b.modelo} onChange={(e) => ponerBat(i, (x) => void (x.modelo = e.target.value))} />
              <CampoTexto etiqueta="Química" value={b.quimica} onChange={(e) => ponerBat(i, (x) => void (x.quimica = e.target.value))} />
              <NumeroCampo etiqueta="kWh nominales por módulo" sufijo="kWh" valor={b.kwh_nominales_por_modulo} alCambiar={(n) => ponerBat(i, (x) => void (x.kwh_nominales_por_modulo = n ?? Number.NaN))} />
              <NumeroCampo
                etiqueta="kWh útiles por módulo"
                sufijo="kWh"
                valor={b.kwh_utiles_por_modulo}
                alCambiar={(n) => ponerBat(i, (x) => void (x.kwh_utiles_por_modulo = n ?? Number.NaN))}
                error={b.kwh_utiles_por_modulo > b.kwh_nominales_por_modulo ? 'No pueden pasar de los nominales.' : undefined}
              />
              <NumeroCampo etiqueta="Módulos máximo" valor={b.modulos_max} alCambiar={(n) => ponerBat(i, (x) => void (x.modulos_max = n ?? Number.NaN))} />
              <NumeroCampo etiqueta="Precio de lista por módulo (sin IVA)" sufijo="$" valor={b.precio_lista_sin_iva_por_modulo} alCambiar={(n) => ponerBat(i, (x) => void (x.precio_lista_sin_iva_por_modulo = n ?? Number.NaN))} />
              <NumeroCampo
                etiqueta="Costo para Solarix por módulo (sin IVA)"
                sufijo="$"
                valor={b.costo_solarix_sin_iva_por_modulo}
                ayuda="Vacío = no se sabe (la rentabilidad sale sin confirmar)"
                alCambiar={(n) => ponerBat(i, (x) => void (x.costo_solarix_sin_iva_por_modulo = n))}
              />
              <NumeroCampo etiqueta="Garantía" sufijo="años" valor={b.garantia_anios} ayuda="La que imprime el PDF sale de la plantilla (garantias.json): Growatt AXE 5 años ampliables a 10; las demás (otras Growatt, Pylontech…), 10" alCambiar={(n) => ponerBat(i, (x) => void (x.garantia_anios = n ?? Number.NaN))} />
            </div>
            <div className="rejilla-2">
              <CampoTexto etiqueta="Fuente" value={b.fuente} onChange={(e) => ponerBat(i, (x) => void (x.fuente = e.target.value))} />
              <CampoTexto etiqueta="Clave" value={b.id} ayuda="Corta, sin espacios (fortress-evault)." onChange={(e) => ponerBat(i, (x) => void (x.id = e.target.value.toLowerCase()))} />
            </div>
            <div className="fila-botones fila-botones--entre">
              <Interruptor etiqueta="Confirmado por Diego" marcado={b.confirmado} alCambiar={(v) => ponerBat(i, (x) => void (x.confirmado = v))} />
              <Boton
                chico
                variante="fantasma"
                icono={<Trash2 aria-hidden />}
                onClick={() =>
                  poner((x) => {
                    x.baterias!.splice(i, 1);
                    if (x.baterias!.length === 0) delete x.baterias;
                  })
                }
              >
                Quitar
              </Boton>
            </div>
          </div>
        ))}
        {invs.map((v, i) => (
          <div key={i} className="hoja pila costo-fijo" aria-label={`Inversor híbrido ${i + 1}`} role="group">
            <p className="costo-fijo__titulo">Inversor híbrido {i + 1}{v.marca || v.modelo ? ` · ${v.marca} ${v.modelo}`.trimEnd() : ''}</p>
            <div className="rejilla-3">
              <CampoTexto etiqueta="Marca" value={v.marca} onChange={(e) => ponerInv(i, (x) => void (x.marca = e.target.value))} />
              <CampoTexto etiqueta="Modelo" value={v.modelo} onChange={(e) => ponerInv(i, (x) => void (x.modelo = e.target.value))} />
              <NumeroCampo etiqueta="Potencia continua" sufijo="kW" valor={v.kw_continuos} alCambiar={(n) => ponerInv(i, (x) => void (x.kw_continuos = n ?? Number.NaN))} />
              {/* Diego, 11-oct noche: el arranque de la casa (3 × su consumo por hora) lo cubre el PICO del inversor. Vacío = se toma
                  la continua (salen más inversores de los que hacen falta). */}
              <NumeroCampo
                etiqueta="Pico de arranque"
                sufijo="kW"
                valor={v.kw_pico ?? null}
                ayuda="De la ficha (p. ej. 2 × Pn). Vacío = se toma la continua"
                alCambiar={(n) => ponerInv(i, (x) => void (x.kw_pico = n))}
                error={v.kw_pico != null && v.kw_pico < v.kw_continuos ? 'No puede ser menor que la continua.' : undefined}
              />
              <NumeroCampo etiqueta="Cambio a respaldo" sufijo="ms" valor={v.ms_cambio} ayuda="Vacío = no se sabe" alCambiar={(n) => ponerInv(i, (x) => void (x.ms_cambio = n))} />
              <NumeroCampo etiqueta="Precio de lista (sin IVA)" sufijo="$" valor={v.precio_lista_sin_iva} alCambiar={(n) => ponerInv(i, (x) => void (x.precio_lista_sin_iva = n ?? Number.NaN))} />
              <NumeroCampo etiqueta="Costo para Solarix (sin IVA)" sufijo="$" valor={v.costo_solarix_sin_iva} ayuda="Vacío = no se sabe" alCambiar={(n) => ponerInv(i, (x) => void (x.costo_solarix_sin_iva = n))} />
              <NumeroCampo etiqueta="Garantía" sufijo="años" valor={v.garantia_anios} alCambiar={(n) => ponerInv(i, (x) => void (x.garantia_anios = n ?? Number.NaN))} />
              <CampoTexto etiqueta="Fuente" value={v.fuente} onChange={(e) => ponerInv(i, (x) => void (x.fuente = e.target.value))} />
              <CampoTexto etiqueta="Clave" value={v.id} ayuda="Corta, sin espacios." onChange={(e) => ponerInv(i, (x) => void (x.id = e.target.value.toLowerCase()))} />
            </div>
            <div className="fila-botones fila-botones--entre">
              <Interruptor etiqueta="Confirmado por Diego" marcado={v.confirmado} alCambiar={(val) => ponerInv(i, (x) => void (x.confirmado = val))} />
              <Boton
                chico
                variante="fantasma"
                icono={<Trash2 aria-hidden />}
                onClick={() =>
                  poner((x) => {
                    x.inversores_hibridos!.splice(i, 1);
                    if (x.inversores_hibridos!.length === 0) delete x.inversores_hibridos;
                  })
                }
              >
                Quitar
              </Boton>
            </div>
          </div>
        ))}
        <div className="fila-botones">
          <Boton chico icono={<Plus aria-hidden />} onClick={() => poner((x) => void (x.baterias = [...(x.baterias ?? []), nuevaBateria(x.baterias ?? [])]))}>
            Agregar batería
          </Boton>
          <Boton chico icono={<Plus aria-hidden />} onClick={() => poner((x) => void (x.inversores_hibridos = [...(x.inversores_hibridos ?? []), nuevoInversor(x.inversores_hibridos ?? [])]))}>
            Agregar inversor híbrido
          </Boton>
        </div>
      </div>
    </Seccion>
  );
}

/** Los tipos a los que aplica un costo fijo: los del motor solar. La ampliación de baterías (27-oct-2026) no pasa por el motor
 *  (sus costos los teclea el vendedor) y la tabla con «ampliacion_baterias» en un costo fijo NO se podría guardar en un servidor
 *  de antes (verificación del 27-oct): ni se ofrece ni se guarda. */
const TIPOS_COSTO_FIJO: readonly TipoProyecto[] = TIPOS_PROYECTO.filter((tp) => tp !== 'ampliacion_baterias');
const soloTiposCostoFijo = (tipos: readonly TipoProyecto[]): TipoProyecto[] => tipos.filter((tp) => TIPOS_COSTO_FIJO.includes(tp));

/** La tabla que se guarda: ningún costo fijo con un tipo que no sea de los del motor (quitar «todos menos uno» que traía la
 *  ampliación deja «todos»). */
export function conTiposCostoFijo<T extends { costos_fijos: CostoFijo[] }>(t: T): T {
  return {
    ...t,
    costos_fijos: t.costos_fijos.map((f) => {
      if (f.aplica.tipos === null) return f;
      const tipos = soloTiposCostoFijo(f.aplica.tipos);
      return { ...f, aplica: { ...f.aplica, tipos: tipos.length === TIPOS_COSTO_FIJO.length ? null : tipos } };
    }),
  };
}

function nuevoFijo(n: number): CostoFijo {
  return {
    id: `fijo-${n}`,
    concepto: '',
    precio_cliente_sin_iva: 0,
    costo_solarix_sin_iva: 0,
    aplica: { tipos: null, tarifas: null, kwp_desde: null, kwp_hasta: null },
    fuente: 'Diego',
    confirmado: true,
  };
}

/** La pantalla de precios es del director: el servidor solo a él le manda la tabla completa (con costos y piso). */
type RespuestaConTabla = RespuestaPrecios & { tabla: NonNullable<RespuestaPrecios['tabla']> };

export function Precios() {
  const [datos, setDatos] = useState<RespuestaConTabla | null>(null);
  const [t, setT] = useState<Cuerpo | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [problemas, setProblemas] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.precios();
      if (!r.tabla) throw new Error('La tabla completa (con costos) solo la ve Diego.');
      setDatos({ ...r, tabla: r.tabla });
      setT(sinMeta(r.tabla));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void cargar();
  }, []);

  if (!t || !datos) return error ? <ErrorEnLinea error={error} /> : <Cargando renglones={8} />;
  const cambiada = JSON.stringify(t) !== JSON.stringify(sinMeta(datos.tabla));
  const poner = (f: (x: Cuerpo) => void) => {
    const c = structuredClone(t);
    f(c);
    setT(c);
    setGuardado(false);
  };

  async function guardar() {
    const r = CuerpoTablaPrecios.safeParse(conTiposCostoFijo(t!));
    if (!r.success) {
      setProblemas(r.error.issues.map(problemaLegible));
      return;
    }
    setProblemas([]);
    setGuardando(true);
    setError(null);
    try {
      await api.guardarPrecios(r.data);
      await cargar();
      setGuardado(true);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  const tab = datos.tabla;
  return (
    <div className="pila pila--grande">
      <div className="aviso-ancho aviso-ancho--info">
        <p>
          <strong>Versión {tab.version}</strong> · vigente desde {fechaHoraCorta(tab.vigente_desde)} · {tab.editada_por === 'sistema' ? 'sembrada por el sistema' : 'editada por el director'}
        </p>
        {tab.aprendido ? (
          <p>
            Aprendida de {tab.aprendido.n_cotizaciones} cotizaciones ({tab.aprendido.periodo}) · {tab.aprendido.metodo} · medido el {tab.aprendido.medido_el}
          </p>
        ) : (
          <p>Tabla manual (no aprendida).</p>
        )}
        {tab.nota && <p className="nota-suave">{tab.nota}</p>}
      </div>

      {/* Diego, 11-oct (MANDA): el precio por watt SIEMPRE en USD/W sin IVA (contado, con el tipo de cambio FIX + colchón aparte);
          el MXN/W solo con una tabla vieja en pesos */}
      <Seccion
        titulo="Precio por watt según el tamaño"
        descripcion={t.tipo_cambio ? 'Contado en USD por watt pico, SIN IVA, llave en mano (la lista es contado ÷ 0.85). El piso solo lo abre Diego.' : 'Precio de LISTA sin IVA por watt pico, llave en mano (tabla vieja en pesos). El contado le quita el descuento de abajo.'}
      >
        <div className="hoja pila">
          {t.rangos.map((r, i) => (
            <div key={i} className="fila-tabla-precio">
              <NumeroCampo chico etiqueta="Desde" sufijo="kWp" valor={r.kwp_desde} alCambiar={(n) => poner((x) => void (x.rangos[i]!.kwp_desde = n ?? Number.NaN))} />
              <NumeroCampo chico etiqueta={i === t.rangos.length - 1 ? 'Hasta (vacío = sin tope)' : 'Hasta'} sufijo="kWp" valor={r.kwp_hasta} alCambiar={(n) => poner((x) => void (x.rangos[i]!.kwp_hasta = n))} />
              {t.tipo_cambio ? (
                <>
                  <NumeroCampo chico etiqueta="Contado" sufijo="USD/W sin IVA" valor={r.contado_usd_por_w ?? null} alCambiar={(n) => poner((x) => void (x.rangos[i]!.contado_usd_por_w = n ?? Number.NaN))} />
                  <NumeroCampo chico etiqueta="Piso" sufijo="USD/W sin IVA" valor={r.piso_contado_usd_por_w ?? null} alCambiar={(n) => poner((x) => void (n === null ? delete x.rangos[i]!.piso_contado_usd_por_w : (x.rangos[i]!.piso_contado_usd_por_w = n)))} />
                </>
              ) : (
                <NumeroCampo chico etiqueta="Precio" sufijo="$/W" valor={r.precio_lista_sin_iva_por_w} alCambiar={(n) => poner((x) => void (x.rangos[i]!.precio_lista_sin_iva_por_w = n ?? Number.NaN))} />
              )}
              <CampoTexto etiqueta="Fuente" value={r.fuente} onChange={(e) => poner((x) => void (x.rangos[i]!.fuente = e.target.value))} className="fila-tabla-precio__fuente" />
              <button type="button" className="icono-boton" aria-label="Quitar rango" disabled={t.rangos.length === 1} onClick={() => poner((x) => void x.rangos.splice(i, 1))}>
                <Trash2 aria-hidden />
              </button>
            </div>
          ))}
          <div className="fila-botones fila-botones--entre">
            <Boton
              chico
              icono={<Plus aria-hidden />}
              onClick={() =>
                poner((x) => {
                  const u = x.rangos[x.rangos.length - 1]!;
                  const desde = u.kwp_hasta ?? u.kwp_desde + 10;
                  if (u.kwp_hasta === null) u.kwp_hasta = desde;
                  x.rangos.push({ kwp_desde: desde, kwp_hasta: null, precio_lista_sin_iva_por_w: u.precio_lista_sin_iva_por_w, ...(u.contado_usd_por_w !== undefined ? { contado_usd_por_w: u.contado_usd_por_w } : {}), ...(u.piso_contado_usd_por_w !== undefined ? { piso_contado_usd_por_w: u.piso_contado_usd_por_w } : {}), fuente: 'Diego' });
                })
              }
            >
              Agregar rango
            </Boton>
            <NumeroCampo chico etiqueta="Descuento de contado" sufijo="%" valor={t.descuento_contado_pct} alCambiar={(n) => poner((x) => void (x.descuento_contado_pct = n ?? Number.NaN))} />
            <NumeroCampo
              chico
              etiqueta="Precio único arriba de (con IVA)"
              sufijo="$"
              valor={t.umbrales.precio_unico_arriba_de_con_iva ?? PRECIO_UNICO_ARRIBA_DE_POR_OMISION}
              // Diego, 13-oct-2026: comercio e industria arriba de esto llevan UN SOLO PRECIO (el de contado), sin lista ni 15 %;
              // ADENDA z5 (14-oct): si UNA opción pasa, van las TRES (un solo criterio por propuesta, también en multisitio)
              ayuda="Comercio e industria: si UNA opción pasa del umbral (su contado con IVA), las TRES van con precio único (todas las opciones de la propuesta, también en multisitio): el de contado, sin lista, sin descuento de contado ni meses sin intereses. Casa y baterías no cambian."
              alCambiar={(n) => poner((x) => void (x.umbrales.precio_unico_arriba_de_con_iva = n ?? Number.NaN))}
            />
            <NumeroCampo
              chico
              etiqueta="Generación (kWh por kWp al año)"
              valor={t.generacion_kwh_kwp ?? 1750}
              // Etapa 3 (Diego, 12-oct): la base de todas las cotizaciones; el vendedor la ajusta por obra y queda en la bitácora
              ayuda="La base de todas las cotizaciones (techo de 15 a 20°, máximo 1,750). El vendedor la ajusta por obra en «Generación por kW» (sol del municipio, inclinación, sombras) y queda en la bitácora."
              alCambiar={(n) => poner((x) => void (x.generacion_kwh_kwp = n ?? Number.NaN))}
            />
          </div>
        </div>
      </Seccion>

      <Seccion titulo="Costos fijos" descripcion="Se suman al precio cuando aplican (por tipo, tarifa o tamaño). UVIE + UI en GDMTO/GDMTH vuelven caro el kW de un sistema chico.">
        <div className="pila">
          {t.costos_fijos.map((f, i) => (
            <div key={i} className="hoja pila costo-fijo">
              <div className="rejilla-3">
                <CampoTexto etiqueta="Concepto" value={f.concepto} onChange={(e) => poner((x) => void (x.costos_fijos[i]!.concepto = e.target.value))} />
                <NumeroCampo etiqueta="Precio al cliente (sin IVA)" sufijo="$" valor={f.precio_cliente_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_fijos[i]!.precio_cliente_sin_iva = n ?? Number.NaN))} ayuda="0 si ya va incluido en el precio por watt" />
                <NumeroCampo etiqueta="Costo para Solarix (sin IVA)" sufijo="$" valor={f.costo_solarix_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_fijos[i]!.costo_solarix_sin_iva = n ?? Number.NaN))} />
              </div>
              <fieldset className="aplica">
                <legend className="campo__etiqueta">Aplica a</legend>
                <div className="aplica__grupo">
                  {TIPOS_COSTO_FIJO.map((tp: TipoProyecto) => (
                    <label key={tp} className="casilla">
                      <input
                        type="checkbox"
                        checked={f.aplica.tipos === null || f.aplica.tipos.includes(tp)}
                        onChange={(e) =>
                          poner((x) => {
                            const a = x.costos_fijos[i]!.aplica;
                            const actuales = soloTiposCostoFijo(a.tipos ?? [...TIPOS_COSTO_FIJO]);
                            const nuevos = e.target.checked ? [...actuales, tp] : actuales.filter((y) => y !== tp);
                            a.tipos = nuevos.length === TIPOS_COSTO_FIJO.length ? null : nuevos;
                          })
                        }
                      />
                      {ETIQUETA_TIPO[tp]}
                    </label>
                  ))}
                </div>
                <div className="aplica__grupo">
                  <label className="casilla">
                    <input type="checkbox" checked={f.aplica.tarifas === null} onChange={(e) => poner((x) => void (x.costos_fijos[i]!.aplica.tarifas = e.target.checked ? null : [...TARIFAS_MEDIA_TENSION]))} />
                    Todas las tarifas
                  </label>
                  {f.aplica.tarifas !== null &&
                    TARIFAS_NEGOCIO.map((tf: Tarifa) => (
                      <label key={tf} className="casilla">
                        <input
                          type="checkbox"
                          checked={f.aplica.tarifas!.includes(tf)}
                          onChange={(e) =>
                            poner((x) => {
                              const a = x.costos_fijos[i]!.aplica;
                              const actuales = a.tarifas ?? [];
                              a.tarifas = e.target.checked ? [...actuales, tf] : actuales.filter((y) => y !== tf);
                            })
                          }
                        />
                        {tf}
                      </label>
                    ))}
                </div>
                <div className="rejilla-2">
                  <NumeroCampo chico etiqueta="Desde" sufijo="kWp" valor={f.aplica.kwp_desde} alCambiar={(n) => poner((x) => void (x.costos_fijos[i]!.aplica.kwp_desde = n))} ayuda="Vacío = desde 0" />
                  <NumeroCampo chico etiqueta="Hasta" sufijo="kWp" valor={f.aplica.kwp_hasta} alCambiar={(n) => poner((x) => void (x.costos_fijos[i]!.aplica.kwp_hasta = n))} ayuda="Vacío = sin tope" />
                </div>
              </fieldset>
              <div className="rejilla-2">
                <CampoTexto etiqueta="Fuente" value={f.fuente} onChange={(e) => poner((x) => void (x.costos_fijos[i]!.fuente = e.target.value))} />
                <CampoTexto etiqueta="Clave" value={f.id} onChange={(e) => poner((x) => void (x.costos_fijos[i]!.id = e.target.value.toLowerCase()))} ayuda="Corta, sin espacios (uvie-ui)." />
              </div>
              <div className="fila-botones fila-botones--entre">
                <Interruptor etiqueta="Confirmado por Diego" marcado={f.confirmado} alCambiar={(v) => poner((x) => void (x.costos_fijos[i]!.confirmado = v))} />
                <Boton chico variante="fantasma" icono={<Trash2 aria-hidden />} onClick={() => poner((x) => void x.costos_fijos.splice(i, 1))}>
                  Quitar
                </Boton>
              </div>
            </div>
          ))}
          <Boton chico icono={<Plus aria-hidden />} onClick={() => poner((x) => void x.costos_fijos.push(nuevoFijo(x.costos_fijos.length + 1)))}>
            Agregar costo fijo
          </Boton>
        </div>
      </Seccion>

      <Seccion titulo="Costos internos" descripcion="Lo que le cuesta a Solarix, para calcular el margen. No se le enseñan al cliente.">
        <div className="hoja pila">
          <div className="rejilla-3">
            <NumeroCampo etiqueta="Equipo (paneles, inversor, estructura)" sufijo="$/W" valor={t.costos_internos.equipo_por_w_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_internos.equipo_por_w_sin_iva = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Mano de obra por panel" sufijo="$" valor={t.costos_internos.mano_obra_por_panel_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_internos.mano_obra_por_panel_sin_iva = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Material eléctrico por kWp" sufijo="$" valor={t.costos_internos.material_electrico_por_kwp_sin_iva} alCambiar={(n) => poner((x) => void (x.costos_internos.material_electrico_por_kwp_sin_iva = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Comisión de venta" sufijo="%" valor={t.costos_internos.comision_venta_pct} alCambiar={(n) => poner((x) => void (x.costos_internos.comision_venta_pct = n ?? Number.NaN))} />
            {/* Diego, 13-oct: «Imprevistos: 2 % del contado sin IVA como COSTO» (baja el margen, no el precio). Hallazgo: este campo
                editaba costos_internos.otros_pct y el motor, con costos por tamaño (producción), usa costos_por_tamano.otros_pct:
                lo que escribía Diego no hacía nada. Ahora enseña el que USA el motor y escribe los dos (así vale en cualquier
                camino del motor, también en baterías). */}
            <NumeroCampo
              etiqueta="Imprevistos (% del contado sin IVA)"
              sufijo="%"
              valor={t.costos_por_tamano ? t.costos_por_tamano.otros_pct : t.costos_internos.otros_pct}
              ayuda="Es un costo: baja el margen, no el precio."
              alCambiar={(n) =>
                poner((x) => {
                  x.costos_internos.otros_pct = n ?? Number.NaN;
                  if (x.costos_por_tamano) x.costos_por_tamano.otros_pct = n ?? Number.NaN;
                })
              }
            />
            <CampoTexto etiqueta="Fuente" value={t.costos_internos.fuente} onChange={(e) => poner((x) => void (x.costos_internos.fuente = e.target.value))} />
          </div>
          <Interruptor etiqueta="Confirmados por Diego" detalle="Mientras no, cada rentabilidad sale marcada con costos sin confirmar." marcado={t.costos_internos.confirmado} alCambiar={(v) => poner((x) => void (x.costos_internos.confirmado = v))} />
        </div>
      </Seccion>

      <Estructuras t={t} poner={poner} />

      <Seccion titulo="Semáforo de rentabilidad">
        <div className="hoja pila">
          <div className="rejilla-3">
            <NumeroCampo etiqueta="Margen mínimo (rojo debajo)" sufijo="%" valor={t.umbrales.margen_minimo_pct} alCambiar={(n) => poner((x) => void (x.umbrales.margen_minimo_pct = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Margen de alerta (amarillo debajo)" sufijo="%" valor={t.umbrales.margen_alerta_pct} alCambiar={(n) => poner((x) => void (x.umbrales.margen_alerta_pct = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Tolerancia del precio por watt" sufijo="± %" valor={t.umbrales.tolerancia_precio_por_w_pct} alCambiar={(n) => poner((x) => void (x.umbrales.tolerancia_precio_por_w_pct = n ?? Number.NaN))} />
            <NumeroCampo etiqueta="Retorno de alerta" sufijo="años" valor={t.umbrales.retorno_alerta_anios} alCambiar={(n) => poner((x) => void (x.umbrales.retorno_alerta_anios = n ?? Number.NaN))} ayuda="Más de 25 años y el PDF no sale." />
          </div>
          {/* Diego, 13-oct: Diego, Miguel y Juan Carlos bajan hasta su tope sin OK aunque el semáforo deje de estar verde; los
              externos, hasta el suyo y solo en verde. Vacío = el de omisión (10 y 5). El piso sigue siendo solo de Diego. */}
          <div className="rejilla-3">
            <NumeroCampo
              etiqueta="Tope sin OK de Diego, Miguel y Juan Carlos"
              sufijo="%"
              valor={t.umbrales.descuento_max_sin_aprobacion_internos_pct ?? null}
              placeholder={String(DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION)}
              ayuda="Bajan hasta este % contra la tabla sin pedir OK, aunque el semáforo deje de estar verde. Debajo del piso, solo tú."
              alCambiar={(n) =>
                poner((x) => {
                  if (n === null) delete x.umbrales.descuento_max_sin_aprobacion_internos_pct;
                  else x.umbrales.descuento_max_sin_aprobacion_internos_pct = n;
                })
              }
            />
            <NumeroCampo
              etiqueta="Tope sin OK de los demás vendedores"
              sufijo="%"
              valor={t.umbrales.descuento_max_sin_aprobacion_pct ?? null}
              placeholder={String(DESCUENTO_MAX_SIN_APROBACION_POR_OMISION)}
              ayuda="Bajan hasta este % sin pedir OK, y solo mientras el semáforo siga en verde."
              alCambiar={(n) =>
                poner((x) => {
                  if (n === null) delete x.umbrales.descuento_max_sin_aprobacion_pct;
                  else x.umbrales.descuento_max_sin_aprobacion_pct = n;
                })
              }
            />
          </div>
          <UmbralesFijos t={t} guardada={datos.tabla} poner={poner} />
        </div>
      </Seccion>

      <Seccion titulo="Panel por omisión">
        <div className="hoja rejilla-3">
          <CampoTexto etiqueta="Marca" value={t.panel_por_omision.marca} onChange={(e) => poner((x) => void (x.panel_por_omision.marca = e.target.value))} />
          <CampoTexto etiqueta="Modelo" value={t.panel_por_omision.modelo} onChange={(e) => poner((x) => void (x.panel_por_omision.modelo = e.target.value))} />
          <NumeroCampo etiqueta="Watts" sufijo="W" valor={t.panel_por_omision.w} alCambiar={(n) => poner((x) => void (x.panel_por_omision.w = n ?? Number.NaN))} />
        </div>
      </Seccion>

      <Respaldo t={t} poner={poner} />

      <CampoTexto etiqueta="Nota de esta versión" value={t.nota ?? ''} maxLength={500} onChange={(e) => poner((x) => void (x.nota = e.target.value.trim() ? e.target.value : null))} />

      {problemas.length > 0 && (
        <div className="error-linea" role="alert">
          <div className="error-linea__cuerpo">
            <p className="error-linea__mensaje">La tabla tiene datos que no sirven:</p>
            <ul className="error-linea__detalles">
              {problemas.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {(cambiada || guardado) && (
      <div className="barra-guardar barra-guardar--fija" role="region" aria-label="Guardar la tabla">
        <span>{cambiada ? 'Cambios sin guardar · se guardará como una versión nueva' : 'Guardado como versión nueva.'}</span>
        <div className="fila-botones">
          {cambiada && (
            <Boton chico variante="fantasma" onClick={() => setT(sinMeta(datos.tabla))}>
              Descartar
            </Boton>
          )}
          <Boton chico variante="principal" icono={<Save aria-hidden />} cargando={guardando} disabled={!cambiada} onClick={() => void guardar()}>
            Guardar tabla
          </Boton>
        </div>
      </div>
      )}

      <Seccion titulo="Versiones anteriores">
        <ul className="lista-simple">
          {datos.historial.map((h) => (
            <li key={h.version}>
              <strong>Versión {h.version}</strong> · {fechaHoraCorta(h.vigente_desde)} · {h.editada_por === 'sistema' ? 'sistema' : 'director'}
              {h.nota ? ` · ${h.nota}` : ''}
              {h.version === tab.version && <Chip tono="verde">vigente</Chip>}
            </li>
          ))}
        </ul>
      </Seccion>
    </div>
  );
}
